/**
 * Card — the base surface container. Apple-style: generous padding, subtle
 * shadow, rounded corners. References semantic tokens only via {@link useTheme}.
 */

import React from 'react';
import { useTheme } from '../theme/index.js';
import type { Theme } from '../tokens/index.js';

export type CardElevation = 'flat' | 'card' | 'raised';

export interface CardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'style'> {
  /** Visual elevation. `flat` has a border, `card`/`raised` add shadow. */
  readonly elevation?: CardElevation;
  /** Render on the raised surface token (e.g. inside a sunken list). */
  readonly raisedSurface?: boolean;
  /** Inner padding (token space scale step). Defaults to step 6 (20px). */
  readonly padding?: keyof Theme['spacing']['space'];
  /** Make the whole card interactive (cursor + hover). */
  readonly interactive?: boolean;
  readonly style?: React.CSSProperties;
  readonly children?: React.ReactNode;
}

export function Card(props: CardProps): React.ReactElement {
  const {
    elevation = 'card',
    raisedSurface = false,
    padding = 6,
    interactive = false,
    style,
    children,
    onMouseEnter,
    onMouseLeave,
    ...rest
  } = props;

  const theme = useTheme();
  const [hover, setHover] = React.useState(false);

  const boxShadow =
    elevation === 'raised'
      ? theme.radii.shadow.raised
      : elevation === 'card'
        ? theme.radii.shadow.card
        : 'none';

  const composedStyle: React.CSSProperties = {
    boxSizing: 'border-box',
    backgroundColor: raisedSurface ? theme.color.surfaceRaised : theme.color.surface,
    border: `1px solid ${theme.color.border}`,
    borderRadius: theme.radii.radius.card,
    padding: theme.spacing.space[padding],
    boxShadow: interactive && hover ? theme.radii.shadow.raised : boxShadow,
    cursor: interactive ? 'pointer' : undefined,
    transition: 'box-shadow 140ms ease',
    ...style,
  };

  return React.createElement(
    'div',
    {
      ...rest,
      style: composedStyle,
      onMouseEnter: interactive
        ? (e: React.MouseEvent<HTMLDivElement>) => {
            setHover(true);
            onMouseEnter?.(e);
          }
        : onMouseEnter,
      onMouseLeave: interactive
        ? (e: React.MouseEvent<HTMLDivElement>) => {
            setHover(false);
            onMouseLeave?.(e);
          }
        : onMouseLeave,
    },
    children,
  );
}
